import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_DETAILS_URL } from "@constants/routes";
import { AXN_COMPANY_ID_PATTERN } from "@constants/url-parameter-patterns";
import { CellDropdownItem } from "@idw-types/common";
import { navigateToUtil } from "@utils/navigation-util";
import { injectCustomIdParameter } from "@utils/parameter-injector";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { AxnCompanyTableDataRow } from "../organizations-table-container";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import styles from "./organization-table-cell.less";
import { entityStatusUpdateModalOptions } from "@constants/entity-modal-options";
import { EntityStatusUpdateModal } from "@containers/modals/status-account-update-modal/entity-status-update-modal";
import { EntityUpdateTypes } from "@enums/entity-update-types";
import { GenericAccountStateTypes } from "@enums/generic-account-state-types";
import { deleteOrganizationReduxRequest } from "@redux-requests/organizations/delete-organization";
import { useDispatch } from "react-redux";
import { restoreOrganizationReduxRequest } from "@redux-requests/organizations/restore-organization";
import { archiveOrganizationReduxRequest } from "@redux-requests/organizations/archive-organization";

type OrganizationTableCellProps = {
    data: AxnCompanyTableDataRow
};

export const OrganizationTableCell = (props: OrganizationTableCellProps) => {
    const {
        data
    } = props;

    const dispatch = useDispatch();

    const modalContext = useModalContext();
    const navigate = useNavigate();
    const routerLocation = useLocation();


    const onClickArchiveOrganization = () => {
        const renderArchiveOrganizationModal = (onModalClose: () => void) => {

            const handleArchiveOrganization = async () => {
                const response = await archiveOrganizationReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions[EntityUpdateTypes.ORGANIZATION]}
                    entityAccountStateType={GenericAccountStateTypes.ARCHIVE}
                    onUpdateStatus={handleArchiveOrganization}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ArchiveOrganization,
            renderFunction: renderArchiveOrganizationModal
        });
    };

    const onClickRestoreOrganization = () => {
        const renderRestoreOrganizationModal = (onModalClose: () => void) => {

            const handleRestoreOrganization = async () => {
                const response = await restoreOrganizationReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions[EntityUpdateTypes.ORGANIZATION]}
                    entityAccountStateType={GenericAccountStateTypes.RESTORE}
                    onUpdateStatus={handleRestoreOrganization}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.RestoreOrganization,
            renderFunction: renderRestoreOrganizationModal
        });
    };


    const onClickDeleteOrganization = () => {
        const renderDeleteOrganizationModal = (onModalClose: () => void) => {

            const handleDeleteOrganization = async () => {
                const response = await deleteOrganizationReduxRequest(dispatch)(data.id);
                return { data: response };
            }

            return (
                <EntityStatusUpdateModal
                    entityModalOptions={entityStatusUpdateModalOptions[EntityUpdateTypes.ORGANIZATION]}
                    entityAccountStateType={GenericAccountStateTypes.DELETE}
                    onUpdateStatus={handleDeleteOrganization}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.DeleteOrganization,
            renderFunction: renderDeleteOrganizationModal
        });
    };

    const handleOpenOrganizationDetails = () => {
        const url = injectCustomIdParameter(
            ORGANIZATION_DETAILS_URL,
            data.id,
            AXN_COMPANY_ID_PATTERN
        );
        navigateToUtil(url, navigate, routerLocation.pathname);
    }

    const organizationDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];
        const prefix = "organization-list";

        const {
            status,
            permissions
        } = data;


        if (data.permissions.canView) {
            items.push({
                idSuffix: `${prefix}-open`,
                title: resources.tables.organizations.dropdownItems.open.label,
                text: resources.tables.organizations.dropdownItems.open.label,
                onClick: handleOpenOrganizationDetails
            });
        }
        
        if (status === EntityStatusTypes.ACTIVE) {
            if (permissions.canArchive) {
                items.push({
                    idSuffix: `${prefix}-archive-organization`,
                    title: resources.tables.organizations.dropdownItems.archiveOrganization.label,
                    text: resources.tables.organizations.dropdownItems.archiveOrganization.label,
                    className: styles.archiveButton,
                    onClick: onClickArchiveOrganization
                });
            }
        } else {
            if (permissions.canRestore) {
                items.push({
                    idSuffix: `${prefix}-restore-organization`,
                    title: resources.tables.organizations.dropdownItems.restoreOrganization.label,
                    text: resources.tables.organizations.dropdownItems.restoreOrganization.label,
                    onClick: onClickRestoreOrganization
                });
            }
        }

        if (permissions.canDelete) {
            items.push({
                idSuffix: `${prefix}-delete-organization`,
                title: resources.tables.organizations.dropdownItems.deleteOrganization.label,
                text: resources.tables.organizations.dropdownItems.deleteOrganization.label,
                className: styles.archiveButton,
                onClick: onClickDeleteOrganization
            });
        }


        return items;
    }, []);

    return (
        <TableCellWithDropdown
            value={data.name}
            dropdownText={""}
            items={organizationDropdownItems}
            uniqueId={data.id}
        />
    );
};